import { useCallback, useContext, useState } from 'react';

import { v4 as uuid } from 'uuid';

import { ClockOutOutsideSettings, TimePeriodSettings } from '@vyce/core/src/types';
import {
  getPeriodSettingsRequest,
  getTimeModulePreferencesRequest,
  setPeriodSettingsRequest,
  updateClockOutOutsideSettingsRequest,
  updateTimeModulePreferencesRequest,
} from '@vyce/core/src/api/time';
import { getCompanySettingsRequest, updateAutoUserDeletionRequest } from '@vyce/core/src/api/companies';

import { timelogDays, TimeLogsPeriods } from '../config';
import { NotificationContext } from '../../../../contexts/notificationContext';

const daysToWeeks = (days: number) => Math.max(1, Math.round(days / 7));

interface Props {
  companyId?: string;
  // Overridable per app: pay-app uses the public/v1 defaults, legend-app passes admin/v1 variants.
  getCompanySettings?: typeof getCompanySettingsRequest;
  updateAutoUserDeletion?: typeof updateAutoUserDeletionRequest;
}

export const useSettingsData = ({
  companyId,
  getCompanySettings = getCompanySettingsRequest,
  updateAutoUserDeletion = updateAutoUserDeletionRequest,
}: Props) => {
  const { handleServerError } = useContext(NotificationContext);
  const [endDay, setEndDay] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [signalForRefreshingId, setSignalForRefreshingId] = useState<string>(uuid());
  const [countAutoClockOut, setCountAutoClockOut] = useState<boolean>(false);
  const [autoDeletionEnabled, setAutoDeletionEnabled] = useState<boolean>(false);
  const [autoDeletionWeeks, setAutoDeletionWeeks] = useState<number>(1);
  const [clockOutOutsideSettings, setClockOutOutsideSettings] = useState<ClockOutOutsideSettings | null>(
    null
  );

  const [settings, setSettings] = useState<TimePeriodSettings>({
    period_type: TimeLogsPeriods.WEEKLY,
    weekday_start: 'monday',
    period_start_date: undefined,
  });

  const getPeriods = async () => {
    if (!companyId) {
      return;
    }
    try {
      setLoading(true);
      const res = await getPeriodSettingsRequest(companyId);
      setSettings(res.data);
      if (res.data.weekday_start) {
        setEndDay(getWeeklyEndDay(res.data.weekday_start));
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  };

  const getPreferences = async () => {
    if (!companyId) {
      return;
    }
    try {
      const res = await getTimeModulePreferencesRequest(companyId);
      setCountAutoClockOut(res.data.count_auto_check_out);
    } catch (e) {
      handleServerError(e);
    }
  };

  const getAutoDeletionSettings = async () => {
    if (!companyId) {
      return;
    }
    try {
      const res = await getCompanySettings(companyId);
      const autoUserDeletion = res.data.time_settings?.auto_user_deletion;
      setAutoDeletionEnabled(autoUserDeletion?.enabled ?? false);
      setAutoDeletionWeeks(daysToWeeks(autoUserDeletion?.inactive_days ?? 7));
    } catch (e) {
      handleServerError(e);
    }
  };

  const saveAutoDeletion = async (enabled: boolean, weeks: number) => {
    if (!companyId) {
      return;
    }
    const prevEnabled = autoDeletionEnabled;
    const prevWeeks = autoDeletionWeeks;
    setAutoDeletionEnabled(enabled);
    setAutoDeletionWeeks(weeks);
    try {
      const res = await updateAutoUserDeletion(companyId, { enabled, inactive_days: weeks * 7 });
      setAutoDeletionEnabled(res.data.enabled);
      setAutoDeletionWeeks(daysToWeeks(res.data.inactive_days));
    } catch (e) {
      setAutoDeletionEnabled(prevEnabled);
      setAutoDeletionWeeks(prevWeeks);
      handleServerError(e);
    }
  };

  const handleToggleAutoDeletion = (enabled: boolean) => saveAutoDeletion(enabled, autoDeletionWeeks);

  const handleAutoDeletionWeeksChange = (weeks: number) => saveAutoDeletion(autoDeletionEnabled, weeks);

  const updateClockOutOutsideSettings = async (countClockOutOutsideSettings: ClockOutOutsideSettings) => {
    if (!companyId) {
      return;
    }
    try {
      setClockOutOutsideSettings(countClockOutOutsideSettings);
      await updateClockOutOutsideSettingsRequest(companyId, countClockOutOutsideSettings);
    } catch (e) {
      handleServerError(e);
    }
  };

  const updatePreferences = async (countAutoClockOut: boolean) => {
    if (!companyId) {
      return;
    }
    setCountAutoClockOut(countAutoClockOut);
    try {
      await updateTimeModulePreferencesRequest(companyId, countAutoClockOut);
    } catch (e) {
      setCountAutoClockOut(!countAutoClockOut);
      handleServerError(e);
    }
  };

  const setNewSignalForRefreshingId = useCallback(() => {
    setSignalForRefreshingId(uuid());
  }, []);

  const getWeeklyEndDay = (startDay: string): string => {
    const dayIndex = timelogDays.findIndex(item => item === startDay);
    let endDayIndex;
    if (dayIndex === 0) {
      endDayIndex = 6;
    } else {
      endDayIndex = dayIndex - 1;
    }
    return timelogDays[endDayIndex];
  };

  const onPeriodChange = async (settings: TimePeriodSettings) => {
    if (!companyId) {
      return;
    }
    setSettings(settings);
    if (settings.weekday_start) {
      setEndDay(getWeeklyEndDay(settings.weekday_start));
    }
    try {
      await setPeriodSettingsRequest(companyId, settings);
      setNewSignalForRefreshingId();
    } catch (e) {
      handleServerError(e);
    }
  };

  return {
    endDay,
    signalForRefreshingId,
    onPeriodChange,
    getPeriods,
    settings,
    loading,
    countAutoClockOut,
    getPreferences,
    updatePreferences,
    setNewSignalForRefreshingId,
    updateClockOutOutsideSettings,
    clockOutOutsideSettings,
    setClockOutOutsideSettings,
    autoDeletionEnabled,
    autoDeletionWeeks,
    getAutoDeletionSettings,
    handleToggleAutoDeletion,
    handleAutoDeletionWeeksChange,
  };
};
